// Phase 5.2 — ONE central Language Registry (client side).
// Mirrors backend/languages.py. Single source of truth for the whole frontend:
// display names, native names, script, text direction and real provider capabilities.
// Import from here instead of hardcoding language lists anywhere.

const DEFAULT_CAPS = {
  can_be_native_language: true,
  can_be_target_language: true,
  interface_locale_available: false, // UI not translated yet (Phase 5.3)
  vision_supported: true,
  text_analysis_supported: true,
  stt_supported: true,
  tts_supported: true,
  transliteration_supported: false, // supplemental; keep native script
  pronunciation_supported: true,
  cefr_supported: true,
  enabled: true,
  english_pair_only: false, // Phase 5.4 — if true, only officially offered paired with English
};

const EN_TARGET_ONLY = { english_pair_only: true, can_be_native_language: false };
const NO_VOICE = { stt_supported: false, tts_supported: false }; // mic / live voice / journal voice / Listen hidden

// [code, english, native, script, direction, flag, overrides]
const RAW = [
  ["en", "English", "English", "Latin", "ltr", "🇬🇧", { interface_locale_available: true }],
  ["es", "Spanish", "Español", "Latin", "ltr", "🇪🇸", {}],
  ["fr", "French", "Français", "Latin", "ltr", "🇫🇷", {}],
  ["de", "German", "Deutsch", "Latin", "ltr", "🇩🇪", {}],
  ["it", "Italian", "Italiano", "Latin", "ltr", "🇮🇹", {}],
  ["pt", "Portuguese", "Português", "Latin", "ltr", "🇵🇹", {}],
  ["sv", "Swedish", "Svenska", "Latin", "ltr", "🇸🇪", { english_pair_only: true }],
  ["nl", "Dutch", "Nederlands", "Latin", "ltr", "🇳🇱", {}],
  ["ja", "Japanese", "日本語", "Japanese", "ltr", "🇯🇵", {}],
  ["zh", "Chinese", "中文", "Han", "ltr", "🇨🇳", {}],
  ["ko", "Korean", "한국어", "Hangul", "ltr", "🇰🇷", {}],
  // ---- Phase 5.2 first global wave ----
  ["el", "Greek", "Ελληνικά", "Greek", "ltr", "🇬🇷", {}],
  ["ru", "Russian", "Русский", "Cyrillic", "ltr", "🇷🇺", {}],
  ["ar", "Arabic", "العربية", "Arabic", "rtl", "🇸🇦", {}],
  ["fa", "Persian", "فارسی", "Arabic", "rtl", "🇮🇷", {}],
  // ---- Phase 5.4 English-paired expansion (offered only paired with English) ----
  ["tr", "Turkish", "Türkçe", "Latin", "ltr", "🇹🇷", { english_pair_only: true }],
  ["sr", "Serbian", "Српски / Srpski", "Cyrillic", "ltr", "🇷🇸",
    { english_pair_only: true, script_choices: ["cyrillic", "latin"], default_script: "cyrillic" }],
  ["hr", "Croatian", "Hrvatski", "Latin", "ltr", "🇭🇷", { english_pair_only: true }],
  ["id", "Indonesian", "Bahasa Indonesia", "Latin", "ltr", "🇮🇩", { english_pair_only: true }],
  ["hu", "Hungarian", "Magyar", "Latin", "ltr", "🇭🇺", { english_pair_only: true }],
  ["fi", "Finnish", "Suomi", "Latin", "ltr", "🇫🇮", { english_pair_only: true }],
  // ---- Phase 7.4A — target-only from English (ckb reserved for future Sorani) ----
  ["pl", "Polish", "Polski", "Latin", "ltr", "🇵🇱", { ...EN_TARGET_ONLY }],
  ["kmr", "Kurdish (Kurmanji)", "Kurmancî", "Latin", "ltr", "🌐", { ...EN_TARGET_ONLY, ...NO_VOICE }],
  ["hi", "Hindi", "हिन्दी", "Devanagari", "ltr", "🇮🇳", { ...EN_TARGET_ONLY }],
  ["th", "Thai", "ไทย", "Thai", "ltr", "🇹🇭", { ...EN_TARGET_ONLY }],
  ["so", "Somali", "Soomaali", "Latin", "ltr", "🇸🇴", { ...EN_TARGET_ONLY, ...NO_VOICE }],
  // ---- Phase 7.4B — target-only from English; tl and fil are separate identities ----
  ["tl", "Tagalog", "Tagalog", "Latin", "ltr", "🇵🇭", { ...EN_TARGET_ONLY }],
  ["fil", "Filipino", "Filipino", "Latin", "ltr", "🇵🇭", { ...EN_TARGET_ONLY, ...NO_VOICE }],
  ["sw", "Swahili", "Kiswahili", "Latin", "ltr", "🇹🇿", { ...EN_TARGET_ONLY, ...NO_VOICE }],
  ["bn", "Bengali", "বাংলা", "Bengali", "ltr", "🇧🇩", { ...EN_TARGET_ONLY, ...NO_VOICE }],
];

export const LANGUAGES = RAW.map(([code, english_name, native_name, script, direction, flag, ov]) => ({
  code,
  english_name,
  // keep `name` for backward-compat with existing imports
  name: english_name,
  native_name,
  script,
  direction,
  flag,
  ...DEFAULT_CAPS,
  ...ov,
}));

const BY_CODE = Object.fromEntries(LANGUAGES.map((l) => [l.code, l]));

export const getLang = (code) =>
  BY_CODE[(code || "").toLowerCase()] || {
    code: code || "",
    english_name: code || "",
    name: code || "",
    native_name: code || "",
    script: "Latin",
    direction: "ltr",
    flag: "🌐",
    ...DEFAULT_CAPS,
  };

export const langName = (code) => getLang(code).english_name;
// Display-only localized name (interface locale); unknown codes fall back to the English name.
const NAME_OVERRIDES = { // keep distinct identities where Intl merges or generalises them
  tl: { es: "tagalo", fr: "tagalog", de: "Tagalog", ar: "التاغالوغية" },
  kmr: { es: "kurdo (kurmanji)", fr: "kurde (kurmandji)", de: "Kurdisch (Kurmandschi)", ar: "الكردية (الكرمانجية)" },
};
export const langDisplayName = (code, locale) => {
  if (!locale || locale === "en") return langName(code);
  if (NAME_OVERRIDES[code]?.[locale]) return NAME_OVERRIDES[code][locale];
  try {
    const n = new Intl.DisplayNames([locale], { type: "language" }).of(code);
    if (n && n !== code) return n;
  } catch { /* fall through */ }
  return langName(code);
};
export const langNative = (code) => getLang(code).native_name;
export const langFlag = (code) => getLang(code).flag;
// "English · العربية" style label (skips native when identical)
export const langLabel = (code) => {
  const l = getLang(code);
  return l.native_name && l.native_name !== l.english_name
    ? `${l.english_name} · ${l.native_name}`
    : l.english_name;
};
export const langDir = (code) => getLang(code).direction;
export const isRtl = (code) => getLang(code).direction === "rtl";
export const supports = (code, cap) => !!getLang(code)[cap];

export const enabledLanguages = () => LANGUAGES.filter((l) => l.enabled);
export const nativeOptions = () => enabledLanguages().filter((l) => l.can_be_native_language);
export const targetOptions = () => enabledLanguages().filter((l) => l.can_be_target_language);

// ---- Phase 5.4 — English-pairing & Serbian script ----
export const englishPairOnly = (code) => !!getLang(code).english_pair_only;

// A (native, target) pair is officially allowed when it's two different languages and —
// if either side is English-pair-only — the other side is English.
export const pairAllowed = (native, target) => {
  const n = (native || "").toLowerCase();
  const t = (target || "").toLowerCase();
  if (!n || !t || n === t) return false;
  if (!getLang(n).can_be_native_language || !getLang(t).can_be_target_language) return false;
  if (englishPairOnly(n) || englishPairOnly(t)) return n === "en" || t === "en";
  return true;
};

// Options for one side, filtered so only valid pairs with the already-chosen partner show.
export const nativeOptionsFor = (targetCode) =>
  nativeOptions().filter((l) => (targetCode ? pairAllowed(l.code, targetCode) : true));
export const targetOptionsFor = (nativeCode) =>
  targetOptions().filter((l) => (nativeCode ? pairAllowed(nativeCode, l.code) : true));

// Phase 7.4A — authoritative voice capabilities (absent control, never a dead one)
export const voiceOK = (code) => !!code && supports(code, "stt_supported");
export const ttsOK = (code) => !!code && supports(code, "tts_supported");

export const isSerbian = (code) => (code || "").toLowerCase() === "sr";
export const SERBIAN_SCRIPTS = [
  { value: "cyrillic", label: "Cyrillic · Ћирилица" },
  { value: "latin", label: "Latin · Latinica" },
];
export const normSerbianScript = (s) =>
  ["cyrillic", "latin"].includes((s || "").toLowerCase()) ? (s || "").toLowerCase() : "cyrillic";

export const CEFR = [
  { level: "A1", label: "Beginner", desc: "I know a few words" },
  { level: "A2", label: "Elementary", desc: "Simple everyday phrases" },
  { level: "B1", label: "Intermediate", desc: "I can hold a basic chat" },
  { level: "B2", label: "Upper-Int.", desc: "I'm fairly fluent" },
  { level: "C1", label: "Advanced", desc: "I'm very comfortable" },
  { level: "C2", label: "Mastery", desc: "Near-native" },
];
